import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { MapPin, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import {
    fetchAddresses,
    createAddress,
    updateAddress,
    deleteAddress
} from '../services/apiService';

const AddressManager = forwardRef(({ onAddressSelect, selectedAddressId, showSelector = false, onAddressUpdate }, ref) => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    const handleEdit = (address) => {
        setEditingAddress({ ...address });
        setIsEditing(true);
        setIsCreating(false);
    };

    const handleCreate = () => {
        setEditingAddress({
            building_name: '',
            building_number: '',
            street_name: '',
            locality: '',
            town_city: '',
            county: '',
            postcode: '',
            country: 'United Kingdom',
            address_type: 'business'
        });
        setIsEditing(true);
        setIsCreating(true);
    };

    // Expose methods for parent components via ref
    useImperativeHandle(ref, () => ({
        handleEdit,
        handleCreate
    }), []);

    useEffect(() => {
        loadAddresses();
    }, []);

    // Reload addresses when showSelector changes (component is shown/hidden)
    useEffect(() => {
        if (showSelector) {
            loadAddresses();
        }
    }, [showSelector]);

    const loadAddresses = async () => {
        try {
            setLoading(true);
            const addressList = await fetchAddresses();
            console.log('AddressManager: Loaded addresses', addressList?.length || 0);
            setAddresses(addressList || []);
        } catch (error) {
            console.error('Error loading addresses:', error);
            alert('Failed to load addresses. Please refresh the page.');
            setAddresses([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            let savedAddress;
            if (isCreating) {
                savedAddress = await createAddress(editingAddress);
                setAddresses([...addresses, savedAddress]);
                setIsCreating(false);
            } else {
                savedAddress = await updateAddress(editingAddress.id, editingAddress);
                setAddresses(addresses.map(addr =>
                    addr.id === savedAddress.id ? savedAddress : addr
                ));
            }
            setEditingAddress(null);
            setIsEditing(false);
            
            // If in selector mode and onAddressSelect is provided, automatically select the saved address
            if (showSelector && onAddressSelect) {
                onAddressSelect(savedAddress);
            }
            
            // Notify parent component of address update
            if (onAddressUpdate) {
                onAddressUpdate(savedAddress);
            }
        } catch (error) {
            console.error('Error saving address:', error);
            alert('Failed to save address. Please check all required fields.');
        }
    };

    const handleDelete = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        try {
            await deleteAddress(addressId);
            setAddresses(addresses.filter(addr => addr.id !== addressId));
        } catch (error) {
            console.error('Error deleting address:', error);
            alert('Failed to delete address. It may be in use by a dealer location.');
        }
    };


    const handleCancel = () => {
        setEditingAddress(null);
        setIsEditing(false);
        setIsCreating(false);
    };

    const formatAddress = (address) => {
        const parts = [
            address.building_name,
            address.building_number,
            address.street_name,
            address.locality,
            address.town_city,
            address.county,
            address.postcode
        ].filter(Boolean);

        return parts.join(', ');
    };

    const renderAddressForm = () => (
        <div className="bg-white p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold mb-4">
                {isCreating ? 'Add New Address' : 'Edit Address'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Building Name</label>
                    <input
                        type="text"
                        value={editingAddress?.building_name || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            building_name: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., Ace Business Centre"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Building Number</label>
                    <input
                        type="text"
                        value={editingAddress?.building_number || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            building_number: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., 123"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Street Name *</label>
                    <input
                        type="text"
                        value={editingAddress?.street_name || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            street_name: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., High Street"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Locality</label>
                    <input
                        type="text"
                        value={editingAddress?.locality || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            locality: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., City Centre"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Town/City *</label>
                    <input
                        type="text"
                        value={editingAddress?.town_city || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            town_city: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., Manchester"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">County</label>
                    <input
                        type="text"
                        value={editingAddress?.county || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            county: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., Greater Manchester"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Postcode</label>
                    <input
                        type="text"
                        value={editingAddress?.postcode || ''}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            postcode: e.target.value.toUpperCase()
                        })}
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., M1 1AA"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Address Type</label>
                    <select
                        value={editingAddress?.address_type || 'business'}
                        onChange={(e) => setEditingAddress({
                            ...editingAddress,
                            address_type: e.target.value
                        })}
                        className="w-full p-2 border rounded-md"
                    >
                        <option value="business">Business</option>
                        <option value="residential">Residential</option>
                        <option value="other">Other</option>
                    </select>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
                <button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 rounded-md flex items-center gap-2"
                >
                    <X size={16} /> Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={!editingAddress?.street_name || !editingAddress?.town_city}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2 disabled:bg-gray-300"
                >
                    <Save size={16} /> Save Address
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">Loading addresses...</div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {isEditing && renderAddressForm()}

            {!isEditing && (
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MapPin size={20} />
                        {showSelector ? 'Select Address' : 'Addresses'}
                    </h3>
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                    >
                        <Plus size={16} /> Add Address
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-3">
                {addresses.map(address => (
                    <div
                        key={address.id}
                        className={`p-4 border rounded-lg ${
                            showSelector && selectedAddressId === address.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        } ${showSelector ? 'cursor-pointer' : ''}`}
                        onClick={() => showSelector && onAddressSelect && onAddressSelect(address)}
                    >
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="font-medium">{formatAddress(address)}</div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {address.country} • {address.address_type}
                                </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent address selection when clicking edit
                                        handleEdit(address);
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit address"
                                >
                                    <Edit size={16} />
                                </button>
                                {!showSelector && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(address.id);
                                        }}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                                        title="Delete address"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {addresses.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    No addresses found. Click "Add Address" to create one.
                </div>
            )}
        </div>
    );
});

AddressManager.displayName = 'AddressManager';

export default AddressManager;